import {
  IEventItem,
  IPermissionItem,
  IPenaltyItem,
  ISettingsItem,
  ITaskItem,
  IDaySchedule,
  IScheduledInfo,
  ITaskSettings,
  IEventSettings,
  IPermissionSettings,
  ICustomSchedule,
  IScheduledItem,
  ICustomScheduleEvent,
  TaskItem,
  PenaltyItem,
  PermissionItem,
  EventItem,
  randomTask,
} from "../ts/Definitions";
import {
  ItemTypes,
  AvailabilityType,
  PreselectType,
  EventType,
  WeekendHandling,
  EventDay,
} from "../ts/enums";
import { isWeekendDay } from "../utilities/dateUtils";
import { getRandomItem } from "../utilities/utils";
import round from "lodash/round";

/**
 * Configuration callbacks for ScheduleManager
 */
export interface ScheduleManagerCallbacks {
  // Item getters
  getTasks: (filter?: (i: ITaskItem) => boolean) => TaskItem[];
  getEvents: (filter?: (i: IEventItem) => boolean) => EventItem[];
  getPermissions: (filter?: (i: IPermissionItem) => boolean) => PermissionItem[];
  getPenalties: (filter?: (i: IPenaltyItem) => boolean) => PenaltyItem[];
  getTask: (key: string) => ITaskItem | undefined;
  getEvent: (key: string) => IEventItem | undefined;
  getPermission: (key: string) => IPermissionItem | undefined;
  getPenalty: (key: string) => IPenaltyItem | undefined;

  // Settings getters
  getTaskSettings: () => ITaskSettings;
  getEventSettings: () => IEventSettings;
  getPermissionSettings: () => IPermissionSettings;
  getSettings: () => { weekendHandling?: WeekendHandling };

  // Validation
  canAddTask: (
    basketItem: ISettingsItem,
    date?: Date,
    bypassDateChecks?: boolean
  ) => { isOk: boolean; reason: string | undefined };
  itemAvailableForDay: (
    item: ISettingsItem,
    eventDay: EventDay,
    includeHidden: boolean,
    ignoreAvailability: boolean
  ) => boolean;
  itemAvailableForDayDiary: (item: ISettingsItem, eventDay: EventDay) => boolean;
  canPerformSomeTasksForEventDay: (dateDay: EventDay) => boolean;
  canPerformAllTasksForEventDay: (day: EventDay) => boolean;
  checkHolidayToday: () => boolean;

  // Category selection
  getTaskForCategory: (
    selectedCategory: string,
    triggerItem: ISettingsItem,
    date?: Date,
    testRun?: boolean
  ) => ITaskItem | undefined;

  // Custom schedules
  getScheduledCustomSchedule: (day: EventDay, date?: Date) => ICustomSchedule | undefined;
  getBusyDays: () => EventDay[];
  peekWeekendDays: () => EventDay[];

  // Other
  describeTask: (
    t: (ITaskItem | IPenaltyItem) & IScheduledInfo,
    day: EventDay,
    from?: ISettingsItem,
    seededCondition?: string,
    seededItemOptions?: Record<string, string>,
    seededItemKeys?: string[],
    seededChoiceName?: string
  ) => (ITaskItem | IPenaltyItem) & IScheduledInfo;
}

/**
 * ScheduleManager - Centralized schedule generation logic
 *
 * Responsibilities:
 * - Generate daily schedules
 * - Determine tasks, events, and permissions for specific days
 * - Handle custom schedules
 * - Calculate task estimates
 * - Manage weekend/weekday logic
 */
export class ScheduleManager {
  private callbacks: ScheduleManagerCallbacks;

  constructor(callbacks: ScheduleManagerCallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Generate schedule for all days
   */
  public getSessionSchedule(includeHidden: boolean): IDaySchedule[] {
    const days: IDaySchedule[] = Object.values(EventDay)
      .filter((d) => !isNaN(Number(d)))
      .map((day) => {
        return this.getDaySessionSchedule(
          day as EventDay,
          includeHidden,
          false,
          false
        );
      });
    return days;
  }

  /**
   * Generate schedule for a specific day
   */
  public getDaySessionSchedule(
    day: EventDay,
    includeHidden: boolean,
    ignoreAvailability: boolean,
    hidePendingItems: boolean
  ): IDaySchedule {
    const busy = this.callbacks.getBusyDays().includes(day);

    // Check for custom schedule
    const customSchedule = this.callbacks.getScheduledCustomSchedule(day);
    if (customSchedule) {
      const scheduleEvents = (customSchedule.events ?? [])
        .map(
          (e, idx) =>
          ({
            ...e,
            key: e.name + idx,
            scheduleReason: `Custom Schedule ${customSchedule.name} selected for day`,
          } as IEventItem & IScheduledInfo)
        )
        .sort((x, y) =>
          x.minTime
            ? x.minTime.localeCompare(y.minTime as string)
            : "0:00".localeCompare(y.minTime as string)
        )
        .map((p) => ({
          ...p,
          name: `${p.name} \n(Chance: ${(p.approvalChance ?? 1) * 100}%)`,
        }));

      const tasks = this.determineTasksForDay(
        day as EventDay,
        scheduleEvents,
        [],
        includeHidden,
        ignoreAvailability,
        hidePendingItems,
        true // Custom schedule: don't include standard tasks
      );

      return {
        day: day as EventDay,
        busy,
        unavailable: !this.callbacks.canPerformSomeTasksForEventDay(
          day as EventDay
        ),
        weekend: this.callbacks.peekWeekendDays().includes(day as EventDay),
        events: scheduleEvents,
        perms: [],
        tasks,
        estHours: this.calcEstimatedTaskHours(tasks.length - 1),
      };
    }

    // Standard schedule
    const events = this.determineEventsForDay(
      day as EventDay,
      includeHidden,
      ignoreAvailability
    );
    const perms = this.determinePermsForDay(
      day as EventDay,
      includeHidden,
      ignoreAvailability
    );
    const tasks = this.determineTasksForDay(
      day as EventDay,
      events,
      perms,
      includeHidden,
      ignoreAvailability,
      hidePendingItems,
      false // Standard schedule: include scheduled tasks
    );

    return {
      day: day as EventDay,
      busy,
      unavailable: !this.callbacks.canPerformSomeTasksForEventDay(
        day as EventDay
      ),
      weekend: this.callbacks.peekWeekendDays().includes(day as EventDay),
      events: events
        .sort((x, y) =>
          x.minTime
            ? x.minTime.localeCompare(y.minTime as string)
            : "0:00".localeCompare(y.minTime as string)
        )
        .map((p) => ({
          ...p,
          name: `${p.name} \n(Chance: ${(p.approvalChance ?? 1) * 100}%)`,
        })),
      perms: perms.map((p) => ({
        ...p,
        name: `${p.name} \n(Chance: ${(p.approvalChance ?? 1) * 100}%)`,
      })),
      tasks,
      estHours: this.calcEstimatedTaskHours(tasks.length - 1),
    };
  }

  /**
   * Determine all tasks for a specific day
   */
  public determineTasksForDay(
    day: EventDay,
    events: IEventItem[] | null,
    perms: IPermissionItem[] | null,
    includeHidden: boolean,
    ignoreAvailability: boolean,
    _hidePendingItems: boolean,
    ignoreScheduleTasks?: boolean
  ): ((ITaskItem | IPenaltyItem) & IScheduledInfo)[] {
    let returnTasks: ((ITaskItem | IPenaltyItem) & IScheduledInfo)[] = [];

    if (
      !ignoreAvailability &&
      (this.callbacks.checkHolidayToday() ||
        !this.callbacks.canPerformSomeTasksForEventDay(day))
    )
      return returnTasks;

    // Add tasks from events
    if (events) {
      events.forEach((event) => {
        this.findTasks(event, day, includeHidden, ignoreAvailability)
          .forEach((task) => {
            // Special handling for custom schedule events with preset conditions
            const scheduleEvent = event as ICustomScheduleEvent;
            let selectedCondition: string | undefined = undefined;
            let selectedItemOptions: Record<string, string> | undefined =
              undefined;
            let selectedItemKeys: string[] | undefined = undefined;
            let selectedChoiceName: string | undefined = undefined;

            if (scheduleEvent && scheduleEvent.itemOptions) {
              const selectedItem = scheduleEvent.itemOptions[task.key];
              const itaskitem = task as ITaskItem;
              const choiceNames = itaskitem.choiceNames;
              const choiceMatch =
                selectedItem && choiceNames
                  ? Object.entries(choiceNames).find(
                    (p) => p[1] === selectedItem
                  )
                  : undefined;

              if (
                selectedItem &&
                choiceMatch &&
                itaskitem.itemOptions &&
                itaskitem.itemsUsed
              ) {
                selectedItemOptions =
                  itaskitem.itemOptions[parseInt(choiceMatch[0])];
                selectedChoiceName = choiceMatch[1];
                selectedItemKeys = itaskitem.itemsUsed[parseInt(choiceMatch[0])];
              } else if (
                selectedItem &&
                selectedItem.startsWith("Option ") &&
                itaskitem.itemsUsed &&
                itaskitem.itemsUsed.length > 0
              ) {
                // "Option X" fallback name — resolve via itemsUsed even without the isChoiceName flag
                const idx = parseInt(selectedItem.replace("Option ", ""));
                if (idx > 0 && itaskitem.itemsUsed[idx - 1]) {
                  if (itaskitem.itemOptions)
                    selectedItemOptions = itaskitem.itemOptions[idx - 1];
                  selectedChoiceName = selectedItem;
                  selectedItemKeys = itaskitem.itemsUsed[idx - 1];
                }
              } else if (selectedItem) {
                selectedCondition = scheduleEvent.itemOptions[task.key];
              } else {
                // Randomly select one of the options if available
                if (itaskitem.itemsUsed) {
                  const idx = Math.floor(
                    Math.random() * itaskitem.itemsUsed.length
                  );
                  if (itaskitem.itemOptions)
                    selectedItemOptions = itaskitem.itemOptions[idx];
                  // Use choiceNames when available, regardless of isChoiceName flag
                  if (choiceNames)
                    selectedChoiceName = choiceNames[idx] || undefined;
                  selectedItemKeys = itaskitem.itemsUsed[idx];
                }
              }
            } else {
              // No itemOptions on this event — deduplicate by key as before
              if (returnTasks.findIndex((s) => s.key === task.key) !== -1)
                return;
            }

            // Deduplicate: allow the same task multiple times only when it has a distinct option
            const isDuplicate = returnTasks.some((s) => {
              if (s.key !== task.key) return false;
              const sCondition = (s as ITaskItem).selectedCondition;
              const sChoice = (s as ITaskItem).selectedChoiceName;
              return sCondition === selectedCondition && sChoice === selectedChoiceName;
            });
            if (isDuplicate) return;

            returnTasks.push(
              this.callbacks.describeTask(
                task,
                day,
                event,
                selectedCondition,
                selectedItemOptions,
                selectedItemKeys,
                selectedChoiceName
              )
            );
          });
      });
    }

    // Add tasks from permissions
    if (perms) {
      perms.forEach((s) => {
        this.findTasks(s, day, includeHidden, ignoreAvailability)
          .filter((t) => returnTasks.findIndex((s) => s.key === t.key) === -1)
          .forEach((t) =>
            returnTasks.push(this.callbacks.describeTask(t, day, s))
          );
      });
    }

    // Add scheduled tasks (if not custom schedule)
    if (
      !ignoreScheduleTasks &&
      !this.callbacks.getTaskSettings().disableSystemTasks
    ) {
      // Mandatory daily tasks
      let dailyMandatoryCredits =
        this.callbacks.getTaskSettings().dailyMandatoryCredits;
      if (isWeekendDay(day, this.callbacks.peekWeekendDays())) {
        const weekendHandling = this.callbacks.getSettings().weekendHandling;
        if (weekendHandling === WeekendHandling.harsh)
          dailyMandatoryCredits += dailyMandatoryCredits;
        else if (weekendHandling === WeekendHandling.easy)
          dailyMandatoryCredits = Math.round(dailyMandatoryCredits / 2);
      }

      for (let i = 0; i < dailyMandatoryCredits; i += 1) {
        returnTasks.push({
          ...randomTask("daily mandatory " + i),
          itemType: ItemTypes.task,
          name: `Daily Task ${i + 1}`,
          unavailable: ignoreAvailability
            ? !this.callbacks.itemAvailableForDayDiary(
              { hidden: false } as ISettingsItem,
              day
            )
            : false,
          scheduleReason:
            "Task is selected by random to take place by the daily mandatory tasks setting and task is valid for given day based on availbility and within kinks and limits for the member.",
        });
      }

      // Preselected tasks
      const allTasks = this.callbacks.getTasks();
      allTasks
        .filter(
          (e) =>
            returnTasks.findIndex((s) => s.key === e.key) === -1 &&
            this.callbacks.canAddTask(e, undefined, true).isOk &&
            this.callbacks.itemAvailableForDay(
              e,
              day,
              includeHidden,
              ignoreAvailability
            ) &&
            (e.preSelect === PreselectType.daily ||
              (e.preSelect === PreselectType.weekdays &&
                !isWeekendDay(day, this.callbacks.peekWeekendDays())) ||
              (e.preSelect === PreselectType.weekends &&
                isWeekendDay(day, this.callbacks.peekWeekendDays())) ||
              e.preSelect === PreselectType.permanent)
        )
        .forEach((t) =>
          returnTasks.push(
            this.callbacks.describeTask(
              {
                ...t,
                name: `${t.name} (${PreselectType[t.preSelect] as string})`,
                unavailable: ignoreAvailability
                  ? !this.callbacks.itemAvailableForDayDiary(t, day)
                  : false,
                scheduleReason:
                  "Task is selected by being scheduled to take place by the preselect setting in the task and task is valid for given day based on availbility and within kinks and limits for the member.",
              },
              day
            )
          )
        );
    }

    // Filter final task list
    returnTasks = returnTasks.filter(
      (t) =>
        t.randomPlaceholder ||
        this.callbacks.itemAvailableForDay(
          t,
          day,
          includeHidden,
          ignoreAvailability
        )
    );

    return returnTasks;
  }

  /**
   * Determine events for a specific day
   */
  private determineEventsForDay(
    day: EventDay,
    includeHidden: boolean,
    ignoreAvailability: boolean
  ): (IEventItem & IScheduledInfo)[] {
    const returnSets: (IEventItem & IScheduledInfo)[] = [];

    if (
      !ignoreAvailability &&
      (this.callbacks.checkHolidayToday() ||
        !this.callbacks.canPerformSomeTasksForEventDay(day))
    )
      return returnSets;

    if (!this.callbacks.getEventSettings().disableEvents) {
      return this.callbacks
        .getEvents()
        .filter(
          (e) =>
            this.callbacks.canAddTask(e, undefined, true).isOk &&
            this.callbacks.itemAvailableForDay(
              e,
              day,
              includeHidden,
              ignoreAvailability
            ) &&
            this.isScheduleItemAvailableDay(e, day)
        )
        .map(
          (p) =>
          ({
            ...p,
            unavailable: ignoreAvailability
              ? !this.callbacks.itemAvailableForDayDiary(p, day)
              : false,
            childTask: true,
            fromKey: p.key,
            scheduleReason:
              "Scheduled event for day specified based on schedule settings and member diary availablity.",
          } as IEventItem & IScheduledInfo)
        );
    }

    return returnSets;
  }

  /**
   * Determine permissions for a specific day
   */
  private determinePermsForDay(
    day: EventDay,
    includeHidden: boolean,
    ignoreAvailability: boolean
  ): (IPermissionItem & IScheduledInfo)[] {
    const returnSets: (IPermissionItem & IScheduledInfo)[] = [];

    if (
      !ignoreAvailability &&
      (this.callbacks.checkHolidayToday() ||
        !this.callbacks.canPerformSomeTasksForEventDay(day))
    )
      return returnSets;

    if (!this.callbacks.getPermissionSettings().disablePermissions) {
      return this.callbacks
        .getPermissions()
        .filter(
          (p) =>
            this.callbacks.canAddTask(p, undefined, true).isOk &&
            this.callbacks.itemAvailableForDay(
              p,
              day,
              includeHidden,
              ignoreAvailability
            ) &&
            p.eventType !== EventType.checkout &&
            this.isScheduleItemAvailableDay(p, day)
        )
        .map(
          (p) =>
          ({
            ...p,
            unavailable: ignoreAvailability
              ? !this.callbacks.itemAvailableForDayDiary(p, day)
              : false,
            childTask: true,
            fromKey: p.key,
            scheduleReason:
              "Scheduled permission for day specified based on schedule settings and member diary availablity.",
          } as IPermissionItem & IScheduledInfo)
        );
    }

    return returnSets;
  }

  /**
   * Find tasks associated with a permission or event
   */
  public findTasks(
    s: IPermissionItem | IEventItem,
    day: EventDay,
    includeHidden: boolean,
    ignoreAvailability: boolean
  ): ((ITaskItem | IPenaltyItem) & IScheduledInfo)[] {
    const returnTasks: ((ITaskItem | IPenaltyItem) & IScheduledInfo)[] = [];

    if (
      !this.callbacks.itemAvailableForDay(
        s,
        day,
        includeHidden,
        false // Don't show tasks for hidden events
      )
    )
      return returnTasks;

    // Handle taskKeys
    if (s.taskKeys && s.taskKeys.length > 0) {
      if (s.pickSingleTask) {
        returnTasks.push({
          ...randomTask("permissionsinglepicktask"),
          name: "One Random Task",
          unavailable: false,
          childTask: true,
          randomSelectedKey: getRandomItem(s.taskKeys),
          fromKey: s.key,
          scheduleReason: `The Permission/Event ${s.name} is set to pick a single task for the day and this task will be added to the schedule and is within the user limits and is available based on the member diary settings.`,
        });
      } else {
        s.taskKeys
          .filter((t) => returnTasks.findIndex((s) => s.key === t) === -1)
          .forEach((t) => {
            const task = this.callbacks.getTask(t);
            if (
              task &&
              this.callbacks.canAddTask(task, undefined, true).isOk &&
              this.callbacks.itemAvailableForDay(
                task,
                day,
                includeHidden,
                ignoreAvailability
              )
            ) {
              returnTasks.push({
                ...task,
                childTask: true,
                fromKey: s.key,
                unavailable: ignoreAvailability
                  ? !this.callbacks.itemAvailableForDayDiary(task, day)
                  : false,
                scheduleReason: `The Permission/Event ${s.name} is set to pick the tasks configured a day and this task will be added to the schedule and is within the user limits and is available based on the member diary settings.`,
              });
            }
          });
      }
    }

    // Handle taskCategories
    if (s.taskCategories && s.taskCategories.length > 0) {
      if (s.pickSingleCategoryTask) {
        returnTasks.push({
          ...randomTask("permissionsinglepickcat"),
          name: `One Random Category Task (from ${s.taskCategories?.join()})`,
          unavailable: false,
          childTask: true,
          randomSelectedKey: this.callbacks.getTaskForCategory(
            getRandomItem(s.taskCategories) as string,
            s
          )?.key,
          fromKey: s.key,
          scheduleReason: `The Permission/Event ${s.name} is set to pick a task for the given categories and this task will be added to the schedule and is within the user limits and is available based on the member diary settings.`,
        });
      } else {
        s.taskCategories.forEach((t) => {
          const tasks = this.callbacks.getTasks(
            (c) =>
              (this.callbacks.canAddTask(c, undefined, true).isOk &&
                this.callbacks.itemAvailableForDay(
                  c,
                  day,
                  includeHidden,
                  ignoreAvailability
                ) &&
                c.categories &&
                c.categories.includes(t)) ||
              false
          );
          if (tasks.length > 0) {
            let overrideName = `${t} Category Task`;
            if ((s.approvalChance ?? 1) < 1)
              overrideName += `\n(Chance: ${(s.approvalChance ?? 1) * 100}%)`;
            returnTasks.push({
              ...randomTask("permissiontaskcat" + t),
              name: overrideName,
              unavailable: false,
              childTask: true,
              randomSelectedKey: this.callbacks.getTaskForCategory(
                t,
                s,
                undefined,
                true
              )?.key,
              fromKey: s.key,
              scheduleReason: `The Permission/Event ${s.name} is set to all tasks for the given categories and this task will be added to the schedule and is within the user limits and is available based on the member diary settings.`,
            });
          }
        });
      }
    }

    // Handle penalties
    if (s.penalties && s.penalties.length > 0) {
      if (s.pickSinglePenalty) {
        returnTasks.push({
          ...randomTask("permissionsinglepickpen"),
          name: "One Random Penalty",
          unavailable: false,
          childTask: true,
          randomSelectedKey: getRandomItem(s.penalties),
          fromKey: s.key,
          scheduleReason: `The Permission/Event ${s.name} is set to pick a single penalty and this penalty will be added to the schedule and is within the user limits and is available based on the member diary settings.`,
        });
      } else {
        s.penalties.forEach((t) => {
          const task = this.callbacks.getPenalty(t);
          if (
            task &&
            this.callbacks.canAddTask(task, undefined, true).isOk &&
            this.callbacks.itemAvailableForDay(
              task,
              day,
              includeHidden,
              ignoreAvailability
            )
          ) {
            returnTasks.push({
              ...task,
              childTask: true,
              fromKey: s.key,
              unavailable: ignoreAvailability
                ? !this.callbacks.itemAvailableForDayDiary(task, day)
                : false,
              scheduleReason: `The Permission/Event ${s.name} is set to pick this penalty and will be added to the schedule and is within the user limits and is available based on the member diary settings.`,
            });
          }
        });
      }
    }

    // Handle pillories (only in preview mode)
    if (ignoreAvailability && s.pillories && s.pillories.length > 0) {
      if (s.pickSinglePillory) {
        returnTasks.push({
          ...randomTask("permissionsinglepickpill"),
          name: "One Random Pillory",
          unavailable: false,
          childTask: true,
          randomSelectedKey: getRandomItem(s.pillories),
          fromKey: s.key,
          scheduleReason: `The Permission/Event ${s.name} is set to pick a single pillory and this pillory will be added to the schedule and is within the user limits and is available based on the member diary settings.`,
        });
      } else {
        s.pillories.forEach((t) => {
          const task = this.callbacks.getPenalty(t);
          if (
            task &&
            this.callbacks.canAddTask(task, undefined, true).isOk &&
            this.callbacks.itemAvailableForDay(
              task,
              day,
              includeHidden,
              ignoreAvailability
            )
          ) {
            returnTasks.push({
              ...task,
              childTask: true,
              fromKey: s.key,
              unavailable: ignoreAvailability
                ? !this.callbacks.itemAvailableForDayDiary(task, day)
                : false,
              scheduleReason: `The Permission/Event ${s.name} is set to pick this pillory and will be added to the schedule and is within the user limits and is available based on the member diary settings.`,
            });
          }
        });
      }
    }

    // Handle miss event penalties
    if (s.missEventPenalties && s.missEventPenalties.length > 0) {
      s.missEventPenalties.forEach((t) => {
        const task = this.callbacks.getPenalty(t);
        if (
          task &&
          this.callbacks.canAddTask(task, undefined, true).isOk &&
          this.callbacks.itemAvailableForDay(
            task,
            day,
            includeHidden,
            ignoreAvailability
          )
        ) {
          returnTasks.push({
            ...task,
            childTask: true,
            fromKey: s.key,
            possiblePenaltyItem: true,
            unavailable: ignoreAvailability
              ? !this.callbacks.itemAvailableForDayDiary(task, day)
              : false,
            scheduleReason: `The Permission/Event ${s.name} is set to assign this penalty for missing this event and will be added to the schedule and is within the user limits and is available based on the member diary settings.`,
          });
        }
      });
    }

    // Handle miss event pillories
    if (
      ignoreAvailability &&
      s.missEventPillories &&
      s.missEventPillories.length > 0
    ) {
      s.missEventPillories.forEach((t) => {
        const task = this.callbacks.getPenalty(t);
        if (
          task &&
          this.callbacks.canAddTask(task, undefined, true).isOk &&
          this.callbacks.itemAvailableForDay(
            task,
            day,
            includeHidden,
            ignoreAvailability
          )
        ) {
          returnTasks.push({
            ...task,
            childTask: true,
            fromKey: s.key,
            possiblePenaltyItem: true,
            unavailable: ignoreAvailability
              ? !this.callbacks.itemAvailableForDayDiary(task, day)
              : false,
            scheduleReason: `The Permission/Event ${s.name} is set to assign this pillory for missing this event and will be added to the schedule and is within the user limits and is available based on the member diary settings.`,
          });
        }
      });
    }

    return returnTasks;
  }

  /**
   * Calculate estimated hours for tasks
   */
  private calcEstimatedTaskHours(taskCount: number): number {
    return round(taskCount * 0.5);
  }

  /**
   * Check if a scheduled item is available for a specific day
   */
  private isScheduleItemAvailableDay(
    p: IScheduledItem,
    day: EventDay
  ): boolean {
    if (p.availability === undefined) return true; // Treat as daily when not available

    return (
      p.availability === AvailabilityType.daily ||
      (p.availability === AvailabilityType.weekdays &&
        !isWeekendDay(day, this.callbacks.peekWeekendDays())) ||
      (p.availability === AvailabilityType.weekends &&
        isWeekendDay(day, this.callbacks.peekWeekendDays())) ||
      (p.availability === AvailabilityType.specific &&
        p.specificDays &&
        p.specificDays.includes(day)) ||
      p.availability === AvailabilityType.random
    );
  }
}
